import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  CircularProgress,
  IconButton,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";

const ChatMessagesSection = () => {
  const [messages, setMessages] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterRoom, setFilterRoom] = useState("");
  const [filterDeleted, setFilterDeleted] = useState("false");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchRooms();
  }, []);

  useEffect(() => {
    fetchMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filterRoom, filterDeleted]);

  const fetchRooms = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/chatrooms`
      );
      setRooms(response.data.rooms);
    } catch (err) {
      console.error("Failed to fetch rooms:", err);
    }
  };

  const fetchMessages = async (search = searchTerm) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page,
        limit: 50,
        search,
        deleted: filterDeleted,
      });
      if (filterRoom) {
        params.append("room_id", filterRoom);
      }
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/chatmessages?${params}`
      );
      setMessages(response.data.messages);
      setTotalPages(response.data.totalPages);
      setTotal(response.data.total);
    } catch (err) {
      console.error("Failed to fetch messages:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchMessages(searchTerm);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/admin/chatmessages/${id}`
      );
      fetchMessages();
    } catch (err) {
      console.error("Failed to delete message:", err);
    }
  };

  if (loading && messages.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress sx={{ color: "#fff" }} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Stats */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}>
          <Paper sx={{ p: 1.5, backgroundColor: "#16213e", borderRadius: 1, border: "1px solid #394867", textAlign: "center" }}>
            <Typography variant="h5" sx={{ color: "#4caf50", fontWeight: 700 }}>{total}</Typography>
            <Typography variant="caption" sx={{ color: "#9e9e9e" }}>Total Messages</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Search and Filter */}
      <Paper sx={{ p: 2, mb: 2, backgroundColor: "#16213e", borderRadius: 2, border: "1px solid #394867" }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search content or username..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleSearch()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#9e9e9e" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  color: "#fff",
                  "& fieldset": { borderColor: "#394867" },
                  "&:hover fieldset": { borderColor: "#541b14" },
                  "&.Mui-focused fieldset": { borderColor: "#541b14" },
                },
              }}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ color: "#9e9e9e" }}>Room</InputLabel>
              <Select
                value={filterRoom}
                label="Room"
                onChange={(e) => { setFilterRoom(e.target.value); setPage(1); }}
                sx={{
                  color: "#fff",
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#394867" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#541b14" },
                  "& .MuiSvgIcon-root": { color: "#9e9e9e" },
                }}
              >
                <MenuItem value="">All Rooms</MenuItem>
                {rooms.map((room) => (
                  <MenuItem key={room.id} value={room.id}>{room.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ color: "#9e9e9e" }}>Status</InputLabel>
              <Select
                value={filterDeleted}
                label="Status"
                onChange={(e) => { setFilterDeleted(e.target.value); setPage(1); }}
                sx={{
                  color: "#fff",
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#394867" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#541b14" },
                  "& .MuiSvgIcon-root": { color: "#9e9e9e" },
                }}
              >
                <MenuItem value="false">Active</MenuItem>
                <MenuItem value="true">Deleted</MenuItem>
                <MenuItem value="">All</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button variant="outlined" onClick={handleSearch} sx={{ color: "#fff", borderColor: "#394867" }}>
                Search
              </Button>
              <IconButton
                onClick={() => fetchMessages()}
                disabled={loading}
                sx={{
                  color: "#9e9e9e",
                  "&:hover": { color: "#fff", backgroundColor: "#394867" },
                }}
                title="Refresh"
              >
                <RefreshIcon
                  sx={{
                    animation: loading ? "spin 1s linear infinite" : "none",
                    "@keyframes spin": {
                      "0%": { transform: "rotate(0deg)" },
                      "100%": { transform: "rotate(360deg)" },
                    },
                  }}
                />
              </IconButton>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Messages Table */}
      <Paper sx={{ backgroundColor: "#16213e", borderRadius: 2, border: "1px solid #394867", overflow: "hidden" }}>
        <TableContainer sx={{ maxHeight: 500 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ backgroundColor: "#0f1729", color: "#9e9e9e", fontWeight: 600, borderBottom: "1px solid #394867", py: 1 }}>Room</TableCell>
                <TableCell sx={{ backgroundColor: "#0f1729", color: "#9e9e9e", fontWeight: 600, borderBottom: "1px solid #394867", py: 1 }}>User</TableCell>
                <TableCell sx={{ backgroundColor: "#0f1729", color: "#9e9e9e", fontWeight: 600, borderBottom: "1px solid #394867", py: 1, width: "40%" }}>Content</TableCell>
                <TableCell sx={{ backgroundColor: "#0f1729", color: "#9e9e9e", fontWeight: 600, borderBottom: "1px solid #394867", py: 1 }}>Status</TableCell>
                <TableCell sx={{ backgroundColor: "#0f1729", color: "#9e9e9e", fontWeight: 600, borderBottom: "1px solid #394867", py: 1 }}>Time</TableCell>
                <TableCell align="center" sx={{ backgroundColor: "#0f1729", color: "#9e9e9e", fontWeight: 600, borderBottom: "1px solid #394867", py: 1 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {messages.map((msg) => (
                <TableRow key={msg.id} hover sx={{ "&:hover": { backgroundColor: "#1a1a2e" }, opacity: msg.is_deleted ? 0.5 : 1 }}>
                  <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867", py: 0.75 }}>
                    <Chip
                      label={msg.room_name || "Unknown"}
                      size="small"
                      sx={{ height: 20, fontSize: "0.7rem", backgroundColor: "#1a1a2e", color: "#9e9e9e", border: "1px solid #394867" }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: "#fff", borderBottom: "1px solid #394867", py: 0.75, fontWeight: 500 }}>
                    {msg.username_snapshot}
                  </TableCell>
                  <TableCell sx={{ color: "#fff", borderBottom: "1px solid #394867", py: 0.75, maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {msg.content}
                  </TableCell>
                  <TableCell sx={{ borderBottom: "1px solid #394867", py: 0.75 }}>
                    <Chip
                      label={msg.is_deleted ? "Deleted" : msg.moderation_status}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.7rem",
                        backgroundColor: msg.is_deleted ? "#b71c1c" : msg.moderation_status === "approved" ? "#1b5e20" : "#e65100",
                        color: "#fff",
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867", py: 0.75, fontSize: "0.75rem" }}>
                    {new Date(msg.created_at).toLocaleString()}
                  </TableCell>
                  <TableCell align="center" sx={{ borderBottom: "1px solid #394867", py: 0.75 }}>
                    {!msg.is_deleted && (
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(msg.id)}
                        sx={{ color: "#ef5350", p: 0.5 }}
                        title="Delete"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {messages.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ color: "#666", textAlign: "center", py: 4, borderBottom: "1px solid #394867" }}>
                    No messages found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 2, p: 2, borderTop: "1px solid #394867" }}>
            <Button
              size="small"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
              sx={{ color: "#fff" }}
            >
              Previous
            </Button>
            <Typography variant="body2" sx={{ color: "#9e9e9e" }}>
              Page {page} of {totalPages}
            </Typography>
            <Button
              size="small"
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
              sx={{ color: "#fff" }}
            >
              Next
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default ChatMessagesSection;
